import React, {useEffect, useState} from 'react';
import {
  Button,
  Col,
  Collapse,
  Flex,
  Form,
  FormInstance,
  InputNumber,
  message,
  Modal,
  Radio,
  Row,
  Space, theme, Typography, Upload, UploadFile, GetProp, UploadProps
} from "antd";
import {t} from "i18next";
import ButtonBlack from "../../../ui/buttons/ButtonBlack";
import {api} from "../../../../api/apiProvider";
import urls from "../../../../api/urls";
import {useParams} from "react-router-dom";
import {Brief, Cost, DataChannel, DataFactor, DataTotal, Period} from "../../../../api/types";
import {subscribeTo} from "../../../../mercure";
import useSWR from "swr";
import {usePeriodDates} from "../../../../hooks/usePeriodDates";
import dayjs, {Dayjs} from "dayjs";
import {TriggerWithArgs} from "swr/mutation";
import {CaretRightOutlined, CloseCircleOutlined} from "@ant-design/icons";
import {CalculationStatus, DataTotalBaseType, DataTotalSource} from "../../../../api/constants";
import {CheckboxGroupProps} from "antd/es/checkbox";

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];
const {Text} = Typography;

interface Form {
  budget: number,
  kpi: number,
}

interface OptimizationFormProps {
  afterSave: Function,
  dataTotalId: number,
  setDataTotalId: Function,
  editable: boolean,
  brief: Brief,
  scrollToBlock: Function,
  mutateDataTotal: TriggerWithArgs<{
    data: DataTotal
  }, any, string, number>,
  period: Period,
  form: FormInstance,
}

interface FormValues {
  baseType: '0' | '1' | '2',
  budgetLimit: number,
  kpiLimit: number,
  factors: FactorValues[],
}

interface FactorCost {
  value: number,
  date: Dayjs,
}

interface FactorValues {
  name: string,
  audience: string,
  responsible: string,
  //metric: string,
  id: number,
  metricMin: number,
  metricMax: number,
  budgetMin: number,
  budgetMax: number,
  cost: number,
  costs: FactorCost[],
}

interface DataFactorPeriodValue {
  date: string;
  budgets: number;
  cost: number;
  metrics: number;
}

interface DataFactorValues {
  name: string;
  dataFactor: string;
  values: DataFactorPeriodValue[][];
}

interface PlanData {
  period: string,
  baseType: number,
  kpiLimit?: string,
  budgetLimit?: string,
  budgetDeviation?: string,
  kpiDeviation?: string,
}

const addDataTotal = async (data: any) => {
  return await api.post(urls.dataTotals, {arg: data})
};

/*const optimizePlan = async (id: number, data: any) => {
  return await api.patch(`${urls.periodPlans}/${id}/optimize`, {arg: data})
}*/

const optimizeDataTotal = async (id: number, data: any) => {
  return await api.patch(`${urls.dataTotals}/${id}/optimize`, {arg: data})
};

const saveDataTotal = async (id: number, data: any) => {
  return await api.patch(`${urls.dataTotals}/${id}/save_data`, {arg: data})
};

const uploadDataFactorsFile = async (id: number, data: any) => {
  return await api.postFile(`${urls.dataTotals}/${id}/upload_dataset`, {arg: data})
};

const options: CheckboxGroupProps<string>['options'] = [
  {label: t('planner.kpi'), value: String(DataTotalBaseType.KPI)},
  {label: t('planner.budget'), value: String(DataTotalBaseType.BUDGET)},
  {label: t('planner.kpiAndBudgetBoth'), value: String(DataTotalBaseType.KPI_AND_BUDGET_BOTH)},
];
export default function OptimizationForm({
                                           brief,
                                           period,
                                           dataTotalId,
                                           setDataTotalId,
                                           afterSave,
                                           editable,
                                           form,
                                           scrollToBlock,
                                           mutateDataTotal,
                                         }: OptimizationFormProps) {
  const routeParams = useParams();
  const periodId = Number(routeParams.period) || 0;
  const [isLoadingAfterCreate, setIsLoadingAfterCreate] = useState<boolean>(false);
  const [isLoadingOptimize, setIsLoadingOptimize] = useState<boolean>(false);
  const [showNpvs, setShowNpvs] = useState<boolean>(false);
  const [showPeriodsCost, setShowPeriodsCost] = useState<number[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isDataFactorsFileUploading, setIsDataFactorsFileUploading] = useState(false);

  //const [dataTotalId, setDataTotalId] = useState<number>(0)
  const [currentValues, setCurrentValues] = useState<FormValues>({
    baseType: '0',
    budgetLimit: 0,
    kpiLimit: 0,
    factors: [],
  });
  const {token} = theme.useToken();

  const isWeekInterval = () => brief.engine.isWeekInterval;

  const {dates: periodDates} = usePeriodDates(period, isWeekInterval());

  const canEdit = () => {
    return editable || dataTotal && dataTotal.data?.dataSource === DataTotalSource.OPTIMIZATION
  };

  const getDataTotalParams = () => {
    const params = new URLSearchParams([
      ['t[]', 'period'], ['t[]', 'period:data'], ['t[]', 'data'], ['t[]', 'data:metrics'], ['t[]', 'data:channels'],
      ['t[]', 'dataChannel'], ['t[]', 'dataChannel:channel'], ['t[]', 'data:file'], ['t[]', 'period:plan'],
      ['t[]', 'media_object:read'], ['t[]', 'dataFactor:factor'], ['t[]', 'factor'], ['t[]', 'channel'],
      ['t[]', 'data:nonMediaDataFactor'], ['channel.isActive', 'true'],
    ]);

    return params.toString()
  };

  const {
    data: dataTotal,
    mutate: updateDataTotal,
  } = useSWR<{
    data: DataTotal
  }>(dataTotalId > 0 ? `${urls.dataTotals}/${dataTotalId}?${getDataTotalParams()}` : null, api.get, {
    revalidateOnFocus: false,
    revalidateIfStale: true,
  });

  const canSaveAndOptimize = () => {
    return dataTotal && dataTotal.data && dataTotal.data.calculationStatus === CalculationStatus.NONE
  };

  useEffect(() => {
    if (dataTotal && dataTotal.data) {
      const factorsValues: FactorValues[] = [];

      const dataChannels = dataTotal.data.dataChannels.filter((dataChannel: DataChannel) => dataChannel.channel.isActive);

      for (let dataChannel of dataChannels as DataChannel[]) {
        const values = {
          name: dataChannel.name,
          audience: dataChannel.channel?.audienceText,
          responsible: dataChannel.channel?.managerText,
          id: dataChannel.id,
          '@id': dataChannel['@id'],
          //metric: dataChannel.factor.metric ? dataChannel.factor.metric.name : '',
          metricMin: dataChannel.metricMin,
          metricMax: dataChannel.metricMax,
          budgetMin: dataChannel.metricMin * dataChannel.cost,
          budgetMax: dataChannel.metricMax * dataChannel.cost,
          cost: dataChannel.cost || 1,
          costs: dataChannel.costs && dataChannel.costs.length > 0 ? dataChannel.costs.map((cost: Cost) => ({
            value: cost.value,
            date: dayjs(cost.date)
          })) : periodDates.map((date: Dayjs) => ({
            value: 0,
            date: date,
          })),
        };

        factorsValues.push(values)
      }

      const dataFactorValues: DataFactorValues[] = [];

      for (let dataFactor of dataTotal.data.nonMediaDataFactors as DataFactor[]) {
        const periods: DataFactorPeriodValue[] = [];
        for (let periodKey in dataFactor.values.periods) {
          const factorValues = dataFactor.values.periods[periodKey];
          periods.push({
            date: periodKey,
            budgets: factorValues.budgets,
            cost: factorValues.cost,
            metrics: factorValues.metrics,
          })
        }
        const periodValues: DataFactorPeriodValue[][] = [];
        for (let i = 0; i < periods.length; i += 6) {
          periodValues.push(periods.slice(i, i + 6));
        }

        const values: DataFactorValues = {
          name: dataFactor.factor.name,
          dataFactor: dataFactor["@id"],
          values: periodValues,
        };

        dataFactorValues.push(values)
      }

      if (dataTotal.data.npvs) {
        form.setFieldValue('npvs', dataTotal.data.npvs.length > 0 ? dataTotal.data.npvs.map((npv: any) => ({
          date: dayjs(npv.date),
          npv: npv.npv,
        })) : periodDates.map((date: Dayjs) => ({
          npv: 0,
          date: date,
        })))
      }

      form.setFieldValue('factors', factorsValues);
      form.setFieldValue('dataFactors', dataFactorValues);
      form.setFieldValue('baseType', String(dataTotal.data.baseType || 0));
      form.setFieldValue('npv', dataTotal.data.npv || 0);
      form.setFieldValue('kpiLimit', dataTotal.data.kpiLimit || 0);
      form.setFieldValue('budgetLimit', dataTotal.data.budgetLimit || 0);
      form.setFieldValue('budgetDeviation', dataTotal.data.budgetDeviation);
      form.setFieldValue('kpiDeviation', dataTotal.data.kpiDeviation);

      setCurrentValues(form.getFieldsValue())
    } else {
      form.setFieldValue('baseType', String(DataTotalBaseType.BUDGET));
      form.setFieldValue('kpiLimit', 0);
      form.setFieldValue('budgetLimit', 0);
      form.setFieldValue('budgetDeviation', 0);
      form.setFieldValue('kpiDeviation', 0)
    }
  }, [dataTotal]);

  useEffect(() => {
    if (!dataTotalId) {
      form.setFieldValue('budgetLimit', 0);
      form.setFieldValue('kpiLimit', 0)
    }
  }, [dataTotalId]);

  const onAfterSave = () => {
    setIsLoadingOptimize(false);
    setDataTotalId(0);
    form.setFieldValue('budgetLimit', 0);
    form.setFieldValue('kpiLimit', 0);
    form.setFieldValue('kpiDeviation', 0);
    form.setFieldValue('budgetDeviation', 0);
    scrollToBlock(dataTotalId);
    afterSave()
  };

  const onFinish = async () => {
    try {
      const values = await form.validateFields();

      if (dataTotalId > 0) {
        const data = {
          npv: values.npv || 1,
          npvs: values.npvs ? values.npvs.map((npv: any) => ({
            date: npv.date.format('YYYY-MM-DD'),
            npv: npv.npv,
          })) : [],
          channels: values.factors.map((factor: FactorValues) => ({
            channel: `/api${urls.dataChannels}/${factor.id}`,
            metricMin: factor.metricMin === 0 ? undefined : factor.metricMin,
            metricMax: factor.metricMax === 0 ? undefined : factor.metricMax,
            cost: factor.cost,
            audienceText: factor.audience,
            managerText: factor.responsible,
            costs: (factor.costs && factor.costs.some((cost: FactorCost) => cost.value)) ? factor.costs.map((cost: FactorCost) => ({
              value: cost.value,
              date: cost.date.format('YYYY-MM-DD')
            })) : [],
          })),
          dataFactors: values.dataFactors ? values.dataFactors.map((dataFactor: DataFactorValues) => ({
            dataFactor: dataFactor.dataFactor,
            metrics: dataFactor.values.flat().map((values: DataFactorPeriodValue) => ({
              value: values.metrics,
              date: values.date,
            })),
          })) : [],
        };

        saveDataTotal(dataTotalId, data).then((response: any) => {
          if (response && response.id) {
            setDataTotalId(response.id);
            updateDataTotal();
            message.success(t('common.saved'))
          }
        }).catch((response) => {
          message.error(response['hydra:description'])
        })
      } else {
        const planData: PlanData = {
          period: `/api${urls.periods}/${periodId}`,
          baseType: values.baseType ? Number(values.baseType) : 1,
        };

        if (values.kpiLimit) {
          planData.kpiLimit = values.kpiLimit
        }

        if (values.budgetLimit) {
          planData.budgetLimit = values.budgetLimit
        }

        if (values.budgetDeviation) {
          planData.budgetDeviation = values.budgetDeviation
        }

        if (values.kpiDeviation) {
          planData.kpiDeviation = values.kpiDeviation
        }

        addDataTotal(planData).then((response: any) => {
          if (response && response.id) {
            setDataTotalId(response.id);
            updateDataTotal();
            mutateDataTotal(response.id);
          }
        }).catch((response) => {
          message.error(response['hydra:description']);
          onAfterSave()
        })
      }
    } catch (error) {
      scrollToFirstError()
    }
  };

  const scrollToFirstError = () => {
    form.validateFields()
      .then(() => {})
      .catch(({ errorFields }) => {
        if (!errorFields?.length) {
          return;
        }

        const firstError = errorFields[0];
        const fieldNamePath = firstError.name;

        form.scrollToField(fieldNamePath, {
          block: 'center',
          behavior: 'smooth',
        });
      });
  };

  const onOptimize = async (event: React.MouseEvent) => {
    const values = await form.validateFields();
    const debugMode = event.altKey || event.ctrlKey;

    if (dataTotalId > 0) {
      const data = {
        npv: values.npv || 1,
        npvs: values.npvs ? values.npvs.map((npv: any) => ({
          date: npv.date.format('YYYY-MM-DD'),
          npv: npv.npv,
        })) : undefined,
        channels: values.factors.map((factor: FactorValues) => ({
          channel: `/api${urls.dataChannels}/${factor.id}`,
          metricMin: factor.metricMin,
          metricMax: factor.metricMax,
          cost: factor.cost,
          costs: (factor.costs && factor.costs.some((cost: FactorCost) => cost.value)) ? factor.costs.map((cost: FactorCost) => ({
            value: cost.value,
            date: cost.date.format('YYYY-MM-DD')
          })) : [],
        })),
        dataFactors: values.dataFactors ? values.dataFactors.map((dataFactor: DataFactorValues) => ({
          dataFactor: dataFactor.dataFactor,
          metrics: dataFactor.values.flat().map((values: DataFactorPeriodValue) => ({
            value: values.metrics,
            date: values.date,
          })),
        })) : [],
        debug: debugMode,
      };

      optimizeDataTotal(dataTotalId, data).then((response: any) => {
        if (debugMode) {
          Modal.confirm({
            title: t('common.save'),
            okText: t('common.save'),
            cancelText: t('common.cancel'),
            onOk: () => {
              saveOptimizerBody(response, dataTotalId);
            },
            onCancel: () => {
            },
          });

        } else {
          setIsLoadingOptimize(true);
          onAfterSave();
          subscribeTo(response.mercureTopic, () => {
            mutateDataTotal(dataTotalId)
          }, (errorMessage: string) => {
            message.error(errorMessage);
            mutateDataTotal(dataTotalId)
          }, (result) => {
            mutateDataTotal(dataTotalId)
          })
        }
      }).catch((response) => {
        message.error(response['hydra:description'])
      })
    }
  };

  const saveOptimizerBody = (data: any, id: number) => {
    try {
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });

      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `optimizer_body_${id}.json`;
      document.body.appendChild(link);

      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error)
    }
  };

  const calculateBudgets = (values: FormValues) => {
    if (values && values.factors && values.factors.length > 0) {
      const factorsValues: FactorValues[] = [];
      for (let currentValue of values.factors) {
        factorsValues.push({
          ...currentValue,
          budgetMin: currentValue.metricMin * currentValue.cost,
          budgetMax: currentValue.metricMax * currentValue.cost,
        })
      }
      form.setFieldValue('factors', factorsValues)
    }
  };

  const formatter = (value: number | string | undefined) => {
    if (!value && value !== 0) return '';

    const numberValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numberValue as number)) return '';

    const [integer, decimal] = numberValue.toString().split('.');
    const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return decimal ? `${formattedInteger},${decimal}` : formattedInteger;
  };

  const parser = (value: string | undefined) => {
    if (!value) return 0;
    const parsedValue = value.replace(/\s/g, '').replace(/,/g, '.');
    return parseFloat(parsedValue) || 0;
  };

  const showKpiInput = () => !currentValues?.baseType || Number(currentValues?.baseType) === DataTotalBaseType.KPI
    || Number(currentValues?.baseType) === DataTotalBaseType.KPI_AND_BUDGET_BOTH;
  const showBudgetInput = () => Number(currentValues?.baseType) === DataTotalBaseType.BUDGET
    || Number(currentValues?.baseType) === DataTotalBaseType.KPI_AND_BUDGET_BOTH;


  const panelStyle: React.CSSProperties = {
    borderRadius: token.borderRadiusSM,
    border: 'none',
  };

  const handleUpload = async () => {
    const formData = new FormData();
    fileList.forEach((file) => {
      formData.append('file', file as FileType);
    });
    setIsDataFactorsFileUploading(true);

    uploadDataFactorsFile(dataTotalId, formData)
    .then(() => {
      message.success(t('common.uploaded'));
      updateDataTotal();
    })
    .catch((response) => {
      message.error(response['hydra:description']);
    })
    .finally(() => {
      setIsDataFactorsFileUploading(false);
      setFileList([]);
    });
  };

  return (
    <Flex className={'optimization'}>
      <Form
        form={form}
        name="dataset-template"
        onFinish={onFinish}
        layout="vertical"
        autoComplete="off"
        size={'middle'}
        style={{width: '100%'}}
        onValuesChange={(_, all) => {
          calculateBudgets(all);
          form.validateFields();
          setCurrentValues(all)
        }}
      >
        <Flex vertical gap={20}>
          <Flex gap={'middle'} style={{width: '100%'}}>
            <Flex gap={6} vertical style={{width: '100%'}}>
              <Flex gap={'middle'} style={{width: '100%'}}>
                <Form.Item
                  name="baseType"
                  rules={[{
                    required: true,
                    message: t('planner.emptyValueLimit'),
                  }]}
                  style={{width: '100%'}}
                >
                  <Radio.Group
                    block
                    options={options}
                    defaultValue="1"
                    optionType="button"
                    buttonStyle="solid"
                    style={{width: '100%'}}
                    disabled={dataTotalId > 0}
                  />
                </Form.Item>
              </Flex>
            </Flex>
          </Flex>
          <Row>
            <Col span={showKpiInput() ? 12 : 0}>
              {showKpiInput() &&
                <Form.Item
                  name="kpiLimit"
                  label={t('planner.expectedKpi')}
                  rules={[{
                    required: true,
                    message: t('planner.emptyKpi'),
                  }]}
                >
                  <InputNumber
                    min={0}
                    controls={false}
                    placeholder={t('common.input')}
                    style={{width: 200}}
                    disabled={dataTotalId > 0}
                    formatter={formatter}
                    parser={parser}
                    precision={2}
                  />
                </Form.Item>
              }
            </Col>
            <Col span={12}>
              {showBudgetInput() &&
                <Form.Item
                  name="budgetLimit"
                  label={t('planner.expectedBudget')}
                  rules={[{
                    required: true,
                    message: t('planner.emptyBudget'),
                  }]}
                >
                  <InputNumber
                    min={0}
                    controls={false}
                    placeholder={t('common.input')}
                    style={{width: 200}}
                    disabled={dataTotalId > 0}
                    formatter={formatter}
                    parser={parser}
                    precision={2}
                  />
                </Form.Item>
              }
            </Col>
          </Row>
          {dataTotalId > 0 &&
            <>
              <span className={'optimization__description'}>{t('planner.fillAllValues')}</span>
              <Flex style={{width: '100%'}} vertical gap={'small'}>
                <label>NPV</label>
                {/*{(!showNpvs && editable) &&*/}
                {canEdit() &&
                  <Form.Item
                    name="npv"
                  >
                    <InputNumber
                      min={0}
                      controls={false}
                      placeholder={t('common.input')}
                      style={{width: 200}}
                      className={'optimization__table-grey'}
                    />
                  </Form.Item>
                }

                {(showNpvs) &&
                  <Form.List name="npvs">
                    {(fields) => (
                      <Flex gap={'middle'} wrap={'wrap'}>
                        {fields.map(({key, name, ...restField}) => (
                          <Flex key={name} vertical align={'center'}>
                            {form.getFieldValue((["npvs", name, "date"])).format('DD.MM.YYYY')}
                            <Form.Item
                              name={[name, 'npv']}
                              rules={[{
                                required: true,
                                type: 'number',
                                validator: (_, value) =>
                                  value > 0 ? Promise.resolve() : Promise.reject(new Error(t('common.input'))),
                              }]}
                            >
                              <InputNumber
                                min={0}
                                controls={false}
                                placeholder={t('common.input')}
                                style={{width: 200}}
                                className={'optimization__table-grey'}
                                disabled={!canEdit()}
                              />
                            </Form.Item>
                          </Flex>
                        ))}
                      </Flex>
                    )}
                  </Form.List>
                }

                {canEdit() &&
                  <Flex gap={'small'}>
                    <Flex className={'optimization__npvs'} onClick={() => setShowNpvs(true)}>
                      {brief.engine.isWeekInterval ? t('planner.weekNpv') : t('planner.monthNpv')}
                    </Flex>
                    {showNpvs &&
                      <Flex className={'optimization__npvs-close'}
                            onClick={() => setShowNpvs(false)}><CloseCircleOutlined
                        size={14}/></Flex>
                    }
                  </Flex>
                }
              </Flex>
            </>
          }

          {dataTotalId > 0 &&
            <>
              <Text className={"channels-block__title"}>{t('planner.dataChannels')}</Text>
              <Form.List name="factors">
                {(fields, {add, remove}) => (
                  <Flex vertical gap={'middle'}>
                    {fields.map(({key, name, ...restField}) => (
                      <Flex key={name} vertical className={'optimization__table'}>
                        <Flex
                          className={'optimization__table-header optimization__table-bordered'}>{form.getFieldValue((["factors", name, "name"]))}</Flex>
                        <Flex style={{width: '100%'}} className={'optimization__table-bordered'}>
                          <Flex style={{width: '50%'}}
                                className={'optimization__table-border-right optimization__table-field'}
                          >
                            <span>
                              {t('planner.audience')}
                            </span>
                            <span>{form.getFieldValue((["factors", name, "audience"]))}</span>
                          </Flex>
                          <Flex style={{width: '50%'}} justify={'space-between'}
                                className={'optimization__table-field'}>
                              <span>
                                {t('planner.responsible')}
                              </span>
                            <span>{form.getFieldValue((["factors", name, "responsible"]))}</span>
                          </Flex>
                        </Flex>
                        <Flex vertical gap={'small'}>
                          <Flex className={'optimization__table-bordered'} style={{width: '100%', marginTop: '-1px'}}>
                            <Flex className={'optimization__table-border-right'} style={{width: '33%'}} vertical>
                              <Flex
                                className={'optimization__table-field-title optimization__table-border-bottom'}>
                                {t('planner.metric')}
                              </Flex>
                              <Flex
                                className={'optimization__table-field optimization__table-grey optimization__table-border-bottom'}>
                                <span>Min:</span>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'metricMin']}
                                  rules={[{
                                    required: true,
                                    type: 'number',
                                    validator: (_, value) =>
                                      !value || value > 0 ? Promise.resolve() : Promise.reject(new Error(t('common.input'))),
                                  }]}
                                >
                                  <InputNumber
                                    variant="borderless"
                                    size={'small'}
                                    controls={false}
                                    disabled={!canEdit()}
                                    style={{width: "100%"}}
                                  />
                                </Form.Item>
                              </Flex>
                              <Flex className={'optimization__table-field optimization__table-grey '} gap={'small'}>
                                <span>Max:</span>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'metricMax']}
                                  rules={[
                                    ({getFieldValue}) => ({
                                      validator(_, value) {
                                        if (getFieldValue((["factors", name, "metricMin"])) <= value) {
                                          return Promise.resolve()
                                        }

                                        return Promise.reject(new Error(`${t('planner.metricMaxError')} ${form.getFieldValue((["factors", name, "name"]))}`))
                                      },
                                    }),
                                  ]}
                                >
                                  <InputNumber
                                    variant="borderless"
                                    size={'small'}
                                    controls={false}
                                    disabled={!canEdit()}
                                    style={{width: "100%"}}
                                  />
                                </Form.Item>
                              </Flex>
                            </Flex>
                            <Flex className={'optimization__table-border-right'} style={{width: '34%'}} vertical>
                              <Flex
                                className={'optimization__table-field-title optimization__table-border-bottom'}>{t('planner.price')}</Flex>
                              <Flex
                                className={'optimization__table-field optimization__table-grey optimization__table-inventory'}>
                                <Flex align={'center'} justify={'flex-end'}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'cost']}
                                    rules={[{
                                      required: true,
                                      type: 'number',
                                      validator: (_, value) =>
                                        value > 0 ? Promise.resolve() : Promise.reject(new Error(t('common.input'))),
                                    }]}
                                  >
                                    <InputNumber
                                      min={0}
                                      variant="borderless"
                                      size={'small'}
                                      precision={8}
                                      decimalSeparator={','}
                                      controls={false}
                                      disabled={!canEdit()}
                                      style={{width: "100%"}}
                                    />
                                  </Form.Item>
                                  <span>{t('common.currency')}</span>
                                </Flex>
                              </Flex>
                              <Flex justify={'center'} style={{width: '100%'}}>
                                {(editable || Array.isArray(form.getFieldValue((["factors", name, "costs"]))) && form.getFieldValue((["factors", name, "costs"])).length > 0) &&
                                  <Flex className={'optimization__npvs'} onClick={
                                    () => {
                                      if (!showPeriodsCost.includes(key)) {
                                        setShowPeriodsCost([
                                          ...showPeriodsCost,
                                          key,
                                        ])
                                      } else {
                                        setShowPeriodsCost([...showPeriodsCost.filter(function (value, valueIndex, arr) {
                                          return value !== key
                                        })])
                                      }
                                    }
                                  }>
                                    {t('planner.periodCost')}
                                  </Flex>
                                }
                              </Flex>
                            </Flex>
                            <Flex style={{width: '33%'}} vertical>
                              <Flex
                                className={'optimization__table-field-title optimization__table-border-bottom'}>{t('planner.budgetWithoutVAT')}</Flex>
                              <Flex
                                className={'optimization__table-field optimization__table-border-bottom'}>
                                <span>Min:</span>
                                <Flex align={'center'}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'budgetMin']}
                                  >
                                    <InputNumber
                                      min={0}
                                      variant="borderless"
                                      size={'small'}
                                      precision={2}
                                      decimalSeparator={','}
                                      controls={false}
                                      disabled={true}
                                      style={{width: "100%"}}
                                    />
                                  </Form.Item>
                                  <span>{t('common.currency')}</span>
                                </Flex>
                              </Flex>
                              <Flex className={'optimization__table-field'}>
                                <span>Max:</span>
                                <Flex align={'center'}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'budgetMax']}
                                  >
                                    <InputNumber
                                      min={0}
                                      variant="borderless"
                                      size={'small'}
                                      precision={2}
                                      decimalSeparator={','}
                                      controls={false}
                                      disabled={true}
                                      style={{width: "100%"}}
                                    />
                                  </Form.Item>
                                  <span>{t('common.currency')}</span>
                                </Flex>
                              </Flex>
                            </Flex>
                          </Flex>

                          {showPeriodsCost && showPeriodsCost.includes(key) &&
                            <Flex>
                              <Form.Item label={''}>
                                <Form.List name={[name, 'costs']}>
                                  {(subFields, subOpt) => (
                                    <Flex gap={'middle'} wrap={'wrap'}>
                                      {subFields.map((subField) => (
                                        <Space key={subField.key}>
                                          <Flex key={subField.name} vertical align={'center'}>
                                            {form.getFieldValue((["factors", name, "costs", subField.name, "date"])).format('DD.MM.YYYY')}
                                            <Form.Item
                                              name={[subField.name, 'value']}
                                            >
                                              <InputNumber
                                                min={0}
                                                controls={false}
                                                placeholder={t('common.input')}
                                                style={{width: 200}}
                                                precision={8}
                                                decimalSeparator={','}
                                                className={'optimization__table-grey'}
                                                disabled={!canEdit()}
                                              />
                                            </Form.Item>
                                          </Flex>
                                        </Space>
                                      ))}
                                    </Flex>
                                  )}
                                </Form.List>
                              </Form.Item>
                            </Flex>
                          }
                        </Flex>
                      </Flex>
                    ))}
                  </Flex>
                )}
              </Form.List>
            </>
          }

          {dataTotalId > 0 &&
            <>
              <Flex align={'center'} justify={'space-between'}>
                <Text className={"channels-block__title"}>{t('planner.dataFactors')}</Text>
                <Upload
                  onRemove={(file) => {
                    const index = fileList.indexOf(file);
                    const newFileList = fileList.slice();
                    newFileList.splice(index, 1);
                    setFileList(newFileList);
                  }}
                  beforeUpload={(file) => {
                    setFileList([...fileList, file]);

                    return false;
                  }}
                  fileList={fileList}
                  onChange={(info) => {
                    handleUpload()
                  }}
                  itemRender={() => <></>}
                  disabled={isDataFactorsFileUploading}
                >
                  <Button
                    loading={isDataFactorsFileUploading}
                  >{t('planner.xlsUpload')}</Button>
                </Upload>

              </Flex>

              <Form.List name="dataFactors">
                {(fields, {add, remove}) => (
                  <Flex vertical gap={'middle'} className={'optimization__table'}>
                    {fields.map(({key, name, ...restField}) => (
                      <Collapse
                        className={'data-factors-collapse'}
                        bordered
                        defaultActiveKey={['1']}
                        expandIcon={({isActive}) => <CaretRightOutlined rotate={isActive ? 90 : 0}/>}
                        items={[
                          {
                            key: 'description',
                            label: <Text
                              className={"collapse-item__title"}
                              style={{width: 800}}
                              ellipsis={{tooltip: form.getFieldValue(["dataFactors", name, "name"])}}>{form.getFieldValue(["dataFactors", name, "name"])}</Text>,
                            children: <Form.List name={[name, 'values']}>
                              {(subFields, subOpt) => (
                                <Flex gap={'middle'} wrap={'wrap'} style={{width: '100%'}}>
                                  <div style={{width: '100%'}}>
                                    {subFields.map((subField) => (
                                      <Row
                                        key={subField.key}
                                        gutter={[0, 0]}
                                        style={{
                                          borderBottom: subField.name < subFields.length - 1 ? '1px solid #d9d9d9' : 'none',
                                          margin: 0
                                        }}
                                      >
                                        <Form.List name={[subField.name]}>
                                          {(subSubFields, subSubOpt) => (
                                            subSubFields.map((subSubField) => {
                                              const hasEmptyCellsAfter = subSubFields.length < 6 && subSubField.name === subSubFields.length - 1;

                                              return <Col
                                                key={subSubField.name}
                                                span={Math.floor(4)}
                                                style={{
                                                  borderRight: hasEmptyCellsAfter || subSubField.name < subSubFields.length - 1 ? '1px solid #d9d9d9' : 'none',
                                                  padding: '8px',
                                                  minHeight: '40px',
                                                  display: 'flex',
                                                  alignItems: 'center'
                                                }}
                                              >
                                                <Flex justify={'space-between'} align={'center'}>
                                                      <span className={"collapse-item__date"}>
                                                        {dayjs(form.getFieldValue(["dataFactors", name, "values", subField.name, subSubField.name, 'date'])).format('DD.MM')}
                                                      </span>
                                                  <Form.Item
                                                    name={[subSubField.name, 'metrics']}
                                                  >
                                                    <InputNumber
                                                      min={0}
                                                      variant="borderless"
                                                      size={'small'}
                                                      precision={2}
                                                      decimalSeparator={','}
                                                      controls={false}
                                                      disabled={!canEdit()}
                                                      style={{width: "100%"}}
                                                      className={'collapse-item__input'}
                                                    />
                                                  </Form.Item>
                                                </Flex>
                                              </Col>
                                            })
                                          )}
                                        </Form.List>
                                      </Row>
                                    ))}
                                  </div>

                                </Flex>
                              )}
                            </Form.List>,
                            style: panelStyle,
                          }
                        ]}
                        expandIconPosition={'end'}
                      />
                    ))}
                  </Flex>
                )}
              </Form.List>
            </>
          }

          <Flex justify={'flex-end'} style={{width: '100%'}} gap={'large'} align={'center'}>
            <Flex>
              {isLoadingAfterCreate && t('planner.waitToPlan')}
              {isLoadingOptimize && t('planner.waitToOptimize')}
            </Flex>
            {canEdit() &&
              <Flex gap={15}>
                <ButtonBlack
                  onClick={onFinish}
                  style={{width: 'auto'}}
                  loading={isLoadingAfterCreate || isLoadingOptimize}
                  disabled={dataTotalId > 0 && !canSaveAndOptimize()}
                >
                  {dataTotalId > 0 ? t('planner.save') : t('common.save')}
                </ButtonBlack>
                {dataTotalId > 0 &&
                  <ButtonBlack
                    onClick={onOptimize}
                    style={{width: 'auto'}}
                    loading={isLoadingAfterCreate || isLoadingOptimize}
                    disabled={!canSaveAndOptimize()}
                  >
                    {t('planner.startOptimization')}
                  </ButtonBlack>
                }
              </Flex>
            }
          </Flex>
        </Flex>
      </Form>
    </Flex>
  )
}
